/**
 * AllInventoryView
 * Full "All Inventory" page — same header/stats/filter/table UI as per-type views,
 * but with all 6 inventory types merged into one unified table.
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Box, Typography, Grid, TextField, InputAdornment, Paper, Chip,
  Button, Stack, useTheme, alpha, IconButton, Tooltip, Fab,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, Alert
} from '@mui/material';
import {
  Search as SearchIcon, Refresh as RefreshIcon,
  Inventory as InventoryIcon, CheckCircle as CheckCircleIcon,
  Warning as WarningIcon, Error as ErrorIcon,
  Print as PrintIcon, Cancel as CancelIcon,
  Tune as TuneIcon
} from '@mui/icons-material';
import { useInventoryData, useInventoryItems, useSuppliers } from '../shared/useInventoryData';
import { getInventoryConfig } from '../shared/inventoryConfig';
import { supabase } from '../../../../supabaseClient';
import AllInventoryTable from './AllInventoryTable';
import PaginationController from '../shared/PaginationController';
import FilterDialogs from '../shared/FilterDialogs';
import AllInventoryAdjustments from './AllInventoryAdjustments';
import { exportAllInventoryReport } from '../shared/inventory-report-components/allInventoryReport';

const ALL_TYPES = ['products', 'chemicals', 'caps', 'bottles', 'labels', 'supplies'];

export default function AllInventoryView({ companyId, locations = [], onShowSnackbar, canEdit = true, canDelete = true }) {
  const theme = useTheme();

  const [searchTerm, setSearchTerm]           = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [page, setPage]                       = useState(0);
  const [rowsPerPage, setRowsPerPage]         = useState(25);
  const [brands, setBrands]                   = useState([]);

  /* ── Adjustments dialog ── */
  const [adjustmentsOpen, setAdjustmentsOpen] = useState(false);
  const { suppliers } = useSuppliers(companyId);

  /* ── Export report dialog ── */
  const [exportReportDialogOpen, setExportReportDialogOpen] = useState(false);
  const [reportType, setReportType] = useState('summary');

  /* ── Location filter dialog ── */
  const [locationFilterDialogOpen, setLocationFilterDialogOpen] = useState(false);
  const [locationSearchTerm, setLocationSearchTerm]             = useState('');
  const [dialogSelectedLocation, setDialogSelectedLocation]     = useState(null);

  const handleApplyLocationFilter = () => {
    setSelectedLocation(dialogSelectedLocation || '');
    setLocationFilterDialogOpen(false);
    setPage(0);
  };

  /* Primary location first, then alphabetical */
  const sortedLocations = useMemo(() =>
    [...(locations || [])].sort((a, b) => {
      if (a.is_main && !b.is_main) return -1;
      if (!a.is_main && b.is_main) return 1;
      return 0;
    }),
  [locations]);

  /* ── Fetch brands for product grouping ── */
  useEffect(() => {
    if (!companyId) return;
    supabase.from('brands').select('id, name, display_order').eq('company_id', companyId)
      .then(({ data }) => { if (data) setBrands(data); });
  }, [companyId]);

  /* ── Fetch product items to resolve brand_id → brand_name ── */
  const { items: productItems } = useInventoryItems('products', companyId);

  /* ── Fetch all 6 types unconditionally (React rules: no conditional hooks) ── */
  const { aggregatedData: productsData,  loading: l1, refresh: r1 } = useInventoryData('products',  companyId, { locationId: selectedLocation || null });
  const { aggregatedData: chemicalsData, loading: l2, refresh: r2 } = useInventoryData('chemicals', companyId, { locationId: selectedLocation || null });
  const { aggregatedData: capsData,      loading: l3, refresh: r3 } = useInventoryData('caps',      companyId, { locationId: selectedLocation || null });
  const { aggregatedData: bottlesData,   loading: l4, refresh: r4 } = useInventoryData('bottles',   companyId, { locationId: selectedLocation || null });
  const { aggregatedData: labelsData,    loading: l5, refresh: r5 } = useInventoryData('labels',    companyId, { locationId: selectedLocation || null });
  const { aggregatedData: suppliesData,  loading: l6, refresh: r6 } = useInventoryData('supplies',  companyId, { locationId: selectedLocation || null });

  const loading = l1 || l2 || l3 || l4 || l5 || l6;

  const refreshAll = useCallback(() => {
    r1(); r2(); r3(); r4(); r5(); r6();
  }, [r1, r2, r3, r4, r5, r6]);

  /* ── Merge all data, tag with _inventoryType, _config, brand_name ── */
  const mergedData = useMemo(() => {
    const TYPE_ORDER = ['products', 'chemicals', 'caps', 'bottles', 'labels', 'supplies'];
    const sets = [
      { type: 'products',  data: productsData  },
      { type: 'chemicals', data: chemicalsData },
      { type: 'caps',      data: capsData      },
      { type: 'bottles',   data: bottlesData   },
      { type: 'labels',    data: labelsData    },
      { type: 'supplies',  data: suppliesData  },
    ];
    const extractSize = (name) => {
      const m = name?.match(/(\d+(\.\d+)?)/);
      return m ? parseFloat(m[1]) : 0;
    };
    const tagged = sets.flatMap(({ type, data }) =>
      (data || []).map(item => {
        const base = { ...item, _inventoryType: type, _config: getInventoryConfig(type) };
        if (type === 'products') {
          const productItem = (productItems || []).find(p => p.id === item.item_id);
          const brandId = productItem?.brand_id || null;
          const brand = brandId ? brands.find(b => b.id === brandId) : null;
          return { ...base, brand_id: brandId, brand_name: brand?.name || null };
        }
        return base;
      })
    );
    return tagged.sort((a, b) => {
      const tA = TYPE_ORDER.indexOf(a._inventoryType);
      const tB = TYPE_ORDER.indexOf(b._inventoryType);
      if (tA !== tB) return tA - tB;
      if (a._inventoryType === 'products') {
        // Sort by brand display_order (unbranded last), then brand name, then product name, then variant size desc
        const bObjA = brands.find(b => b.id === a.brand_id);
        const bObjB = brands.find(br => br.id === b.brand_id);
        const orderA = bObjA?.display_order ?? 9999;
        const orderB = bObjB?.display_order ?? 9999;
        if (orderA !== orderB) return orderA - orderB;
        const brandA = (a.brand_name || '\uffff').toLowerCase();
        const brandB = (b.brand_name || '\uffff').toLowerCase();
        if (brandA !== brandB) return brandA.localeCompare(brandB);
        const nameA = (a.item_name || '').toLowerCase();
        const nameB = (b.item_name || '').toLowerCase();
        if (nameA !== nameB) return nameA.localeCompare(nameB);
        const varA = (a.variant_name || '').toLowerCase();
        const varB = (b.variant_name || '').toLowerCase();
        if (varA !== varB) {
          const sizeA = extractSize(a.variant_name || a.item_name);
          const sizeB = extractSize(b.variant_name || b.item_name);
          return sizeB - sizeA;
        }
        // Same product + variant at multiple locations — primary location first
        const locA = (locations || []).find(l => l.id === a.location_id);
        const locB = (locations || []).find(l => l.id === b.location_id);
        return (locA?.is_main ? 0 : 1) - (locB?.is_main ? 0 : 1);
      }
      // chemicals, caps, bottles, labels, supplies — sort by name then primary location first
      const nameA = (a.item_name || '').toLowerCase();
      const nameB = (b.item_name || '').toLowerCase();
      if (nameA !== nameB) return nameA.localeCompare(nameB);
      const locA = (locations || []).find(l => l.id === a.location_id);
      const locB = (locations || []).find(l => l.id === b.location_id);
      return (locA?.is_main ? 0 : 1) - (locB?.is_main ? 0 : 1);
    });
  }, [productsData, chemicalsData, capsData, bottlesData, labelsData, suppliesData, productItems, brands, locations]);

  /* ── byType — raw arrays for the report export ── */
  const byType = useMemo(() => ({
    products:  productsData  || [],
    chemicals: chemicalsData || [],
    caps:      capsData      || [],
    bottles:   bottlesData   || [],
    labels:    labelsData    || [],
    supplies:  suppliesData  || [],
  }), [productsData, chemicalsData, capsData, bottlesData, labelsData, suppliesData]);

  /* ── Filter ── */
  const filteredData = useMemo(() => {
    const s = searchTerm.toLowerCase();
    return mergedData.filter(item => {
      const matchSearch = !s ||
        item.item_name?.toLowerCase().includes(s) ||
        item.item_sku?.toLowerCase().includes(s);
      const matchLoc = !selectedLocation || item.location_id === selectedLocation;
      return matchSearch && matchLoc;
    });
  }, [mergedData, searchTerm, selectedLocation]);

  /* ── Stats ── */
  const stats = useMemo(() => ({
    total:    filteredData.length,
    inStock:  filteredData.filter(i => (i.total_quantity || 0) > 0).length,
    lowStock: filteredData.filter(i => {
      const q = i.total_quantity || 0, rp = i.reorder_point || 0;
      return rp > 0 && q > 0 && q <= rp;
    }).length,
    outStock: filteredData.filter(i => (i.total_quantity || 0) <= 0).length,
  }), [filteredData]);

  /* ── Pagination ── */
  const paginatedData = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredData.slice(start, start + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  const statCards = [
    { label: 'Total Inventory Items', value: stats.total,    color: 'info',    icon: <InventoryIcon sx={{ fontSize: 32 }} /> },
    { label: 'In Stock',              value: stats.inStock,  color: 'success', icon: <CheckCircleIcon sx={{ fontSize: 32 }} /> },
    { label: 'Low Stock',             value: stats.lowStock, color: 'warning', icon: <WarningIcon sx={{ fontSize: 32 }} /> },
    { label: 'Out of Stock',          value: stats.outStock, color: 'error',   icon: <ErrorIcon sx={{ fontSize: 32 }} /> },
  ];

  return (
    <Box>
      {/* Header card */}
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        mb: 3, p: 3, borderRadius: 4,
        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: 'white',
        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.08)}`,
        position: 'relative', overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
        },
        '&::after': {
          content: '""', position: 'absolute', top: 0, right: 0,
          width: '200px', height: '200px',
          background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 70%)`,
          borderRadius: '50%', transform: 'translate(50%, -50%)', zIndex: 0
        }
      }}>
        <Box sx={{ position: 'relative', zIndex: 1, flex: 1 }}>
          {/* Live tracking badge */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box sx={{
              width: 12, height: 12, borderRadius: '50%', bgcolor: 'success.main',
              animation: 'pulse 2s infinite',
              '@keyframes pulse': { '0%': { opacity: 1 }, '50%': { opacity: 0.5 }, '100%': { opacity: 1 } }
            }} />
            <Typography variant="caption" sx={{
              color: 'success.main', fontWeight: 500,
              textTransform: 'uppercase', letterSpacing: '1px', fontSize: '0.75rem'
            }}>
              Live Tracking
            </Typography>
          </Box>

          <Typography variant="h4" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '1.8rem', lineHeight: 1.2, mb: 1 }}>
            All Inventory
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.9rem', mb: 2.5, maxWidth: '400px' }}>
            Real-time inventory tracking with lot management, supplier documentation, and reorder alerts
          </Typography>

          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Chip label={`${filteredData.length} Items`} size="small"
              sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: 'info.main', fontWeight: 500, border: `1px solid ${alpha(theme.palette.info.main, 0.2)}` }} />
            <Chip label={`${stats.inStock} In Stock`} size="small"
              sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: 'success.main', fontWeight: 500, border: `1px solid ${alpha(theme.palette.success.main, 0.2)}` }} />
            <Chip label={`${stats.lowStock} Low Stock`} size="small"
              sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), color: 'warning.main', fontWeight: 500, border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}` }} />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', position: 'relative', zIndex: 1 }}>
          <Tooltip title="Refresh all inventory">
            <IconButton onClick={refreshAll} sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              color: 'primary.main',
              '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.15), transform: 'rotate(180deg)', transition: 'all 0.4s ease' }
            }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>

          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={() => setExportReportDialogOpen(true)}
            sx={{
              borderRadius: 2, px: 3, py: 1.2, fontSize: '0.9rem', fontWeight: 500,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              color: 'text.primary',
              '&:hover': {
                bgcolor: alpha(theme.palette.grey[100], 0.8),
                border: `1px solid ${alpha(theme.palette.grey[600], 0.8)}`,
                transform: 'translateY(-1px)'
              }
            }}
          >
            Export Report
          </Button>

          <Button
            variant="contained"
            startIcon={<TuneIcon />}
            onClick={() => setAdjustmentsOpen(true)}
            disabled={!canEdit}
            sx={{
              borderRadius: 2, px: 3, py: 1.2, fontSize: '0.9rem', fontWeight: 500,
              bgcolor: 'black',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.8)', transform: 'translateY(-1px)' }
            }}
          >
            Manage Inventory
          </Button>
        </Box>
      </Box>

      {/* Stat cards */}
      <Grid container spacing={3} sx={{ mb: 5 }}>
        {statCards.map((s, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Paper sx={{
              height: '100%', p: 3,
              background: alpha(theme.palette[s.color].main, 0.06),
              border: `1px solid ${theme.palette[s.color].main}`,
              borderRadius: 3,
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
              transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 20px 40px ${alpha(theme.palette[s.color].main, 0.25)}`
              }
            }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="h3" sx={{
                    fontWeight: 500, color: `${s.color}.main`, mb: 1,
                    fontSize: { xs: '1.4rem', sm: '1.6rem', md: '1.8rem' }, lineHeight: 1
                  }}>
                    {s.value}
                  </Typography>
                  <Typography variant="h6" color="text.primary" sx={{ fontWeight: 500, fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem' } }}>
                    {s.label}
                  </Typography>
                </Box>
                <Box sx={{
                  p: { xs: 1.5, sm: 2 }, borderRadius: 3,
                  background: theme.palette[s.color].main, color: 'white',
                  boxShadow: `0 6px 18px ${alpha(theme.palette[s.color].main, 0.3)}`,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                }}>
                  {s.icon}
                </Box>
              </Stack>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Filter bar */}
      <Paper sx={{
        p: 4, mb: 4, borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
        boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
        }
      }}>
        <Grid container spacing={3} alignItems="flex-end">
          {/* Search */}
          <Grid item xs={12} md={4}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.secondary', fontSize: '0.9rem' }}>
              Search
            </Typography>
            <TextField
              fullWidth
              placeholder="Search inventory..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: theme.palette.primary.main }} />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: 3, border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white', height: 56,
                  '&:hover': { border: `1px solid ${theme.palette.primary.main}` },
                  '&.Mui-focused': { border: `1px solid ${theme.palette.primary.main}` }
                }
              }}
              sx={{
                '& .MuiInputBase-input': { fontSize: '1rem', fontWeight: 500, py: 2, px: 2 },
                '& .MuiOutlinedInput-root': { height: 56, '& fieldset': { border: 'none' } }
              }}
            />
          </Grid>

          {/* Location */}
          <Grid item xs={12} md={4}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.secondary', fontSize: '0.9rem' }}>
              Location
            </Typography>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => { setDialogSelectedLocation(selectedLocation || null); setLocationFilterDialogOpen(true); }}
              sx={{
                height: 56, borderRadius: 3, bgcolor: 'white', justifyContent: 'flex-start', px: 2,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                color: selectedLocation ? 'text.primary' : 'text.secondary',
                fontWeight: selectedLocation ? 500 : 400,
                textTransform: 'none',
                '&:hover': { border: `1px solid ${theme.palette.primary.main}`, bgcolor: 'white' }
              }}
            >
              {selectedLocation
                ? sortedLocations.find(l => l.id === selectedLocation)?.name || 'Unknown Location'
                : 'All Locations'}
            </Button>
          </Grid>

          {/* Items found */}
          <Grid item xs={12} md={4}>
            <Box sx={{
              p: 2, borderRadius: 2, height: 56,
              bgcolor: alpha(theme.palette.primary.main, 0.05),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              textAlign: 'center', display: 'flex', flexDirection: 'column',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}` }
            }}>
              <Typography variant="h6" sx={{ fontWeight: 500, color: 'primary.main', fontSize: '1.1rem', lineHeight: 1 }}>
                {filteredData.length}
              </Typography>
              <Typography variant="body2" sx={{ color: 'black', fontWeight: 500, fontSize: '0.85rem' }}>
                Items Found
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Table + pagination */}
      <Paper sx={{
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
        boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
        }
      }}>
        <Box sx={{ px: 1, py: 1, display: 'flex', justifyContent: 'flex-start' }}>
          <PaginationController
            count={filteredData.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(rpp) => { setRowsPerPage(rpp); setPage(0); }}
            rowsPerPageOptions={[25, 50, 100]}
          />
        </Box>
        <AllInventoryTable data={paginatedData} onRefresh={refreshAll} brands={brands} onBrandsChange={setBrands} canEdit={canEdit} canDelete={canDelete} />

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 6, px: 3 }}>
          <Paper sx={{
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
            overflow: 'hidden'
          }}>
            <PaginationController
              count={filteredData.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(rpp) => { setRowsPerPage(rpp); setPage(0); }}
              rowsPerPageOptions={[25, 50, 100]}
            />
          </Paper>
        </Box>
      </Paper>

      {/* Floating Action Button */}
      {canEdit && createPortal(
        <Fab
          color="primary"
          aria-label="manage inventory"
          onClick={() => setAdjustmentsOpen(true)}
          sx={{
            position: 'fixed',
            bottom: 32,
            right: 32,
            width: 80,
            height: 80,
            boxShadow: theme.shadows[6],
            zIndex: 9999,
            '&:hover': {
              transform: 'scale(1.05)',
              boxShadow: theme.shadows[8]
            }
          }}
        >
          <TuneIcon sx={{ fontSize: 28 }} />
        </Fab>,
        document.body
      )}

      {/* Manage Inventory */}
      <AllInventoryAdjustments
        open={adjustmentsOpen}
        onClose={() => setAdjustmentsOpen(false)}
        companyId={companyId}
        locations={locations}
        suppliers={suppliers || []}
        onSuccess={(msg, severity) => { setAdjustmentsOpen(false); refreshAll(); onShowSnackbar?.(msg, severity); }}
      />

      {/* Export Report Dialog */}
      <Dialog
        open={exportReportDialogOpen}
        onClose={() => setExportReportDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex', alignItems: 'center', gap: 2,
          p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`
        }}>
          <PrintIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 500 }}>Export Report</Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', fontWeight: 500 }}>
            Choose the report format you want to export:
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Report Type</InputLabel>
            <Select value={reportType} label="Report Type" onChange={(e) => setReportType(e.target.value)}>
              <MenuItem value="summary">Summary (Total Quantities Only)</MenuItem>
              <MenuItem value="detailed">Detailed (With Lot/Batch Breakdown)</MenuItem>
            </Select>
          </FormControl>
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            {reportType === 'summary'
              ? 'This report will show aggregated totals for all inventory types grouped by section.'
              : 'This report will include a detailed breakdown of all lot numbers and batch numbers for every item.'}
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
          <Button
            onClick={() => setExportReportDialogOpen(false)}
            startIcon={<CancelIcon />}
            sx={{ borderRadius: 2, px: 3, py: 1, fontWeight: 500 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              try {
                await exportAllInventoryReport(companyId, byType, reportType);
                setExportReportDialogOpen(false);
              } catch (error) {
                console.error('Error exporting report:', error);
              }
            }}
            startIcon={<PrintIcon />}
            sx={{
              borderRadius: 2, px: 3, py: 1, fontWeight: 500,
              bgcolor: 'primary.main',
              boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
              '&:hover': {
                bgcolor: 'primary.dark',
                transform: 'translateY(-1px)',
                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
              }
            }}
          >
            Generate Report
          </Button>
        </DialogActions>
      </Dialog>

      <FilterDialogs
        locationFilterDialogOpen={locationFilterDialogOpen}
        setLocationFilterDialogOpen={setLocationFilterDialogOpen}
        locationSearchTerm={locationSearchTerm}
        setLocationSearchTerm={setLocationSearchTerm}
        locations={sortedLocations}
        dialogSelectedLocation={dialogSelectedLocation}
        setDialogSelectedLocation={setDialogSelectedLocation}
        onApplyLocationFilter={handleApplyLocationFilter}
      />
    </Box>
  );
}
